import { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  buildDefinitionFromStore,
  deriveCategoryFromAnalyticalVariable,
  selectCriteria,
  selectIsEditable,
  useCohortDefinitionStore,
  type AnalyticalVariableListItem,
} from '@verantos-dev/feature-analytical-variables';
import {
  AnchorType,
  VariableOutputType,
  type IndexDateResolution,
} from '@verantos-dev/types-cohorts';

import { useEmitResearchAssistantUpdate } from '../../hooks/common/useEmitResearchAssistantUpdate';
import { useAvLibraryItems } from '../AddVariableFromLibraryModal/useAvLibraryItems';

export type IndexDateTab = 'variable' | 'calendar';
export type OccurrenceType = 'first' | 'nth' | 'last';

/**
 * Returns true if the library item is eligible to be used as an index date.
 * Only AVs with Date output type are eligible (OR-6120).
 * AVs whose SQL references {index_date_source} are excluded — they depend on
 * the index date and selecting them would create a circular reference (AC2).
 *
 * SQL-only AVs (jsonValue === null, gen3 path): eligible when format === 'date' and sqlBody
 * does not contain {index_date_source}.
 * Structured AVs (jsonValue !== null, legacy path): eligible when output type is BOOLEAN,
 * window anchors are not INDEX_DATE, and the variable is clinical (not demographic/observability).
 */
export function isEligibleForIndexDate(item: AnalyticalVariableListItem): boolean {
  // OR-6638: a variable declared row_level="event" returns one row per
  // qualifying event, not one value per patient — never eligible as an index
  // date.
  if (item.rowLevel === 'event') return false;
  if (item.jsonValue === null) {
    if (item.indexDependent ?? item.sqlBody?.includes('{index_date_source}')) return false;
    return item.format.toLowerCase() === 'date';
  }
  const av = item.jsonValue;
  if (av.output.type !== VariableOutputType.BOOLEAN) return false;
  const { start, end } = av.window;
  if (start.anchor === AnchorType.INDEX_DATE || end.anchor === AnchorType.INDEX_DATE) return false;
  return deriveCategoryFromAnalyticalVariable(av) === 'clinical';
}

export const useSetIndexDateModal = (onClose: () => void) => {
  const [tab, setTab] = useState<IndexDateTab>('variable');
  const [selectedAvId, setSelectedAvId] = useState<string | null>(null);
  const [occurrence, setOccurrence] = useState<OccurrenceType>('first');
  const [nthValue, setNthValue] = useState<string>('2');
  const [calendarDate, setCalendarDate] = useState<Dayjs | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const isGen3 = useFeatureFlagSplit('research_assistant_gen_3');

  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();
  const setIndexDateFromLibrary = useCohortDefinitionStore((s) => s.setIndexDateFromLibrary);
  const setIndexDateFromLibraryReference = useCohortDefinitionStore(
    (s) => s.setIndexDateFromLibraryReference
  );
  const setCalendarDateIndexDate = useCohortDefinitionStore((s) => s.setCalendarDateIndexDate);
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const criteria = useCohortDefinitionStore(selectCriteria);

  const {
    allItems,
    isLoading,
    isError,
    inCohortAvIds,
    nameConflictAvIds,
    searchQuery,
    handleSearchChange,
    filterBySearch,
  } = useAvLibraryItems();

  const hasIndexDate = useMemo(
    () => criteria.some((c) => c.criteriaType === 'index_date'),
    [criteria]
  );

  // When FF is off, exclude SQL-only AVs (jsonValue === null): the clone path
  // requires a structured definition to copy.
  const eligibleItems = useMemo(
    () =>
      allItems.filter((item) => {
        if (!isGen3 && item.jsonValue === null) return false;
        // Blocking AV-IR until migration is complete
        if (isGen3 && item.sqlBody === null) return false;
        return isEligibleForIndexDate(item);
      }),
    [allItems, isGen3]
  );

  const filteredItems = useMemo(
    () => filterBySearch(eligibleItems),
    [eligibleItems, filterBySearch]
  );

  const handleTabChange = useCallback((newTab: IndexDateTab) => {
    setTab(newTab);
    setSelectedAvId(null);
    setSubmitError(false);
  }, []);

  const handleSelectAv = useCallback((avId: string) => {
    setSelectedAvId((prev) => (prev === avId ? null : avId));
    setSubmitError(false);
  }, []);

  const handleOccurrenceChange = useCallback((value: OccurrenceType) => {
    setOccurrence(value);
  }, []);

  const handleNthValueChange = useCallback((value: string) => {
    setNthValue(value);
  }, []);

  const handleNthValueBlur = useCallback(() => {
    setNthValue((prev) => {
      const parsed = parseInt(prev, 10);
      if (Number.isNaN(parsed) || parsed < 1) return '1';
      return String(parsed);
    });
  }, []);

  const handleCalendarDateChange = useCallback((_id: string, value: Dayjs | null) => {
    setCalendarDate(value);
    setSubmitError(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!isEditable) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      if (tab === 'calendar') {
        if (!calendarDate) return;
        setCalendarDateIndexDate(calendarDate.format('YYYY-MM-DD'));
      } else {
        if (!selectedAvId) return;
        const item = allItems.find((i) => i.analyticalVariableId === selectedAvId);
        if (!item) throw new Error('Library analytical variable not found');
        const parsedNth = parseInt(nthValue, 10);
        const safeNth = Number.isNaN(parsedNth) || parsedNth < 1 ? 1 : parsedNth;
        const dateResolution: IndexDateResolution =
          occurrence === 'nth' ? { occurrence: 'nth', nthValue: safeNth } : { occurrence };
        if (isGen3) {
          // TODO: cleanup with research_assistant_gen_3 ff
          // Reference-by-id: criterion carries sourceLibraryAvId only; runner
          // resolves AV body at execution time.
          setIndexDateFromLibraryReference(selectedAvId, dateResolution);
        } else {
          if (!item.jsonValue) throw new Error('Missing analytical variable definition');
          const newAv = { ...item.jsonValue, id: crypto.randomUUID() };
          setIndexDateFromLibrary(newAv, dateResolution, selectedAvId);
        }
      }
      emitResearchAssistantUpdate(buildDefinitionFromStore());
      onClose();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isGen3,
    isEditable,
    tab,
    calendarDate,
    selectedAvId,
    occurrence,
    nthValue,
    allItems,
    setCalendarDateIndexDate,
    setIndexDateFromLibrary,
    setIndexDateFromLibraryReference,
    emitResearchAssistantUpdate,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const canSubmit =
    isEditable && !isSubmitting && (tab === 'calendar' ? !!calendarDate : !!selectedAvId);

  return {
    tab,
    selectedAvId,
    occurrence,
    nthValue,
    calendarDate,
    isSubmitting,
    submitError,
    searchQuery,
    isLoading,
    isError,
    filteredItems,
    hasIndexDate,
    inCohortAvIds,
    nameConflictAvIds,
    canSubmit,
    isGen3,
    handleTabChange,
    handleSelectAv,
    handleOccurrenceChange,
    handleNthValueChange,
    handleNthValueBlur,
    handleCalendarDateChange,
    handleSearchChange,
    handleSubmit,
    handleClose,
  };
};
